import { useCallback } from 'react';
import { shareCatalog, shareProduct } from '../tools/utils';
import { createCatalog } from 'actions/channel';
import request from '../tools/request';
import { PAYMENT_METHODS } from '../tools/constants';
import { attachUserCatalog, updateCatalog } from 'actions/user';
import { useDispatch, useSelector } from 'react-redux';
import useTranslation from './useTranslation';
import countries from 'tools/constants/countries.json';
import { useRouterWithLang } from 'hooks/useRouterWithLang';

const useShareOnSocial = ({ product, catalog }) => {
  const router = useRouterWithLang();
  const isLoggedIn = useSelector((s) => s.isLoggedIn);
  const PERMISSIONS = useSelector((state) => state.permissions);
  const freeCatalog = useSelector((state) => state?.user?.userCatalogs)?.[0];
  const referralCode = useSelector(
    (state) => state?.user?.referralSettings?.userCode
  );
  const { TRANSLATIONS, locale } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user);

  const shareOnSocial = useCallback(async () => {
    const _catalog = PERMISSIONS.IS_FREE_USER ? freeCatalog : catalog;
    if (!isLoggedIn || referralCode) {
      if (product) {
        shareProduct({
          title: TRANSLATIONS.products.list.share,
          text: TRANSLATIONS.products.list.shareSubtitle,
          product,
          referralCode,
        });
      } else {
        const query = router.query;
        delete query.id;
        delete query.page;
        delete query.tab;
        if (referralCode) {
          query.referralCode = referralCode;
        }
        shareCatalog({
          title: TRANSLATIONS.products.list.share,
          text: TRANSLATIONS.products.list.shareSubtitle,
          query,
        });
      }
    } else if (_catalog && _catalog?.connections?.length !== 0) {
      shareProduct({
        title: TRANSLATIONS.products.list.share,
        text: TRANSLATIONS.products.list.shareSubtitle,
        catalog: _catalog,
        product,
      });
    } else {
      if (PERMISSIONS.IS_PRIVATE && !_catalog) {
        const currency =
          countries.find((country) => country.countrycode === user.countrycode)
            ?.currency || 'EUR';
        dispatch(
          createCatalog({
            ids: [],
            connections: [
              { service: 'socialEcommerce', component: 'socialEcommerce' },
            ],
            name: 'Wishlist',
            language: locale,
            currency,
            minQuantity: 1,
            allProducts: true,
            onSuccess: async (newCatalog) => {
              request(`/api/user_catalog/${newCatalog?._id}/socialEcommerce`, {
                method: 'POST',
                data: {
                  name: 'Wishlist',
                  paymentType: PAYMENT_METHODS.CREDIT,
                },
              }).then(async (responseUpdate) => {
                await dispatch(attachUserCatalog());
                shareProduct({
                  title: TRANSLATIONS.products.list.share,
                  text: TRANSLATIONS.products.list.share,
                  catalog: responseUpdate?.data,
                  product,
                });
              });
            },
          })
        );
      } else {
        if (PERMISSIONS.IS_PRIVATE && _catalog?.connections?.length === 0) {
          dispatch(
            updateCatalog(
              _catalog?._id,
              {
                connections: [
                  { service: 'socialEcommerce', component: 'socialEcommerce' },
                ],
                minQuantity: 1,
                allProducts: true,
              },
              () => {
                shareProduct({
                  title: TRANSLATIONS.products.list.share,
                  text: TRANSLATIONS.products.list.shareSubtitle,
                  catalog: _catalog,
                  product,
                });
              }
            )
          );
        }
      }
    }
  }, [
    PERMISSIONS.IS_FREE_USER,
    PERMISSIONS.IS_PRIVATE,
    TRANSLATIONS.products.list.share,
    TRANSLATIONS.products.list.shareSubtitle,
    catalog,
    dispatch,
    freeCatalog,
    isLoggedIn,
    locale,
    product,
    referralCode,
    router.query,
    user.countrycode,
  ]);

  return {
    shareOnSocial,
  };
};

export default useShareOnSocial;
